import {
  UiEvents,
  usePlayerFromContext,
} from '@loomhq/createhq/dist/video-player';
import React from 'react';

import OverlayStateProvider, { useOverlayStore } from './OverlayStoreProvider';
import { useFetchComments } from '../hooks/comments';

const CommentsModal = React.lazy(
  () => import(/* webpackChunkName: "CommentsOverlay" */ './CommentsOverlay')
);

const CommentsOverlayLazy = (): JSX.Element => {
  return (
    <React.Suspense fallback={null}>
      <OverlayStateProvider>
        <CommentsOverlay />
      </OverlayStateProvider>
    </React.Suspense>
  );
};

const CommentsOverlay = () => {
  const player = usePlayerFromContext();
  const { comments } = useFetchComments();
  const isPlaying = React.useRef(false);
  const [commentsSelected, setCommentsSelected] = React.useState<string[]>([]);
  const [hasOverlayOpenedOnce, setHasOverlayOpenedOnce] =
    React.useState<boolean>(false);
  const { overlayOpen, setOverlayOpen } = useOverlayStore();

  // reset selected state when overlay closes
  React.useEffect(() => {
    if (overlayOpen) {
      setHasOverlayOpenedOnce(true);

      return;
    }

    setCommentsSelected([]);
  }, [overlayOpen, setHasOverlayOpenedOnce]);

  // register for comment click event
  React.useEffect(() => {
    if (!player) {
      return;
    }

    isPlaying.current = !player.paused;
    const handleCommentEvent = selectedComments => {
      isPlaying.current = !player.paused;
      setOverlayOpen(true);
      setCommentsSelected(selectedComments);
    };

    player?.on([UiEvents.commentClicked], handleCommentEvent);

    return () => {
      player?.off([UiEvents.commentClicked], handleCommentEvent);
    };
  }, [player, setOverlayOpen]);

  React.useEffect(() => {
    player?.media.setAttribute('data-active', overlayOpen ? 'false' : 'true');

    if (isPlaying.current) {
      player?.togglePlay();
    }
  }, [overlayOpen]);

  return (
    <>
      {hasOverlayOpenedOnce ? (
        <CommentsModal
          comments={comments}
          commentsSelected={commentsSelected}
          overlayOpen={overlayOpen}
        />
      ) : undefined}
    </>
  );
};

export default CommentsOverlayLazy;
